import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { woundAPI, reportAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import HealingTrendChart from '@/components/HealingTrendChart';
import WoundPhotoGallery from '@/components/WoundPhotoGallery';
import { ArrowLeft, Plus, FileText, Calendar, Ruler, Activity, Download, AlertTriangle, TrendingUp, Minus, ChevronDown, ChevronUp, Camera, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function WoundDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [wound, setWound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);

  useEffect(() => { loadWound(); }, [id]);

  const loadWound = async () => {
    try {
      const res = await woundAPI.get(id);
      setWound(res.data);
    } catch { navigate(-1); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getReportTrend = (report, index, reports) => {
    if (index === reports.length - 1) return null; // first report
    const prev = reports[index + 1]; // sorted desc, so next in array is previous in time
    if (!prev) return null;
    const areaDiff = (report.flaeche || 0) - (prev.flaeche || 0);
    if (areaDiff < -0.5) return { icon: TrendingUp, label: 'Besserung', color: 'text-emerald-600' };
    if (areaDiff > 0.5) return { icon: AlertTriangle, label: 'Verschlechterung', color: 'text-rose-600' };
    return { icon: Minus, label: 'Stagnation', color: 'text-amber-600' };
  };

  const handleExportPdf = (reportId) => {
    const url = reportAPI.exportPdf(reportId);
    window.open(url, '_blank');
  };

  const handleEditReport = (e, reportId) => {
    e.stopPropagation();
    navigate(`/wunde/${id}/bericht/${reportId}/bearbeiten`);
  };

  const handleDeleteReport = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm('Wundbericht wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await reportAPI.delete(reportId);
      toast.success('Bericht gelöscht');
      loadWound();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  if (!wound) return null;

  const reports = wound.reports || [];

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4" data-testid="wound-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(wound.patient ? `/patienten/${wound.patient_id}` : -1)} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-slate-900 truncate">{wound.wundart}</h1>
          <p className="text-sm text-slate-500">{wound.patient?.name} &middot; {wound.lokalisation}</p>
        </div>
        <Badge variant="outline" className={`border shrink-0 ${wound.status === 'aktiv' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
          {wound.status === 'aktiv' ? 'Offen' : 'Verheilt'}
        </Badge>
      </div>

      {/* Summary Card */}
      {reports.length > 0 && (
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-slate-900">{reports[0]?.flaeche || 0}</p>
                <p className="text-xs text-slate-500">cm² aktuell</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reports[0]?.schmerz_nrs || 0}</p>
                <p className="text-xs text-slate-500">NRS Schmerz</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{reports.length}</p>
                <p className="text-xs text-slate-500">Berichte</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Report Button */}
      <Button
        data-testid="new-report-btn"
        onClick={() => navigate(`/wunde/${id}/neuer-bericht`)}
        className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
      >
        <Plus className="w-4 h-4 mr-2" />
        {reports.length > 0 ? 'Folgebericht erstellen' : 'Ersten Bericht erstellen'}
      </Button>

      {/* Tabs */}
      <Tabs defaultValue="verlauf" className="w-full">
        <TabsList className="w-full bg-slate-100 rounded-xl p-1 h-auto">
          <TabsTrigger value="verlauf" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-verlauf">
            Verlauf
          </TabsTrigger>
          <TabsTrigger value="fotos" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-fotos">
            <Camera className="w-3.5 h-3.5 mr-1" />Fotos
          </TabsTrigger>
          <TabsTrigger value="trend" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-trend">
            Heilungstrend
          </TabsTrigger>
        </TabsList>

        <TabsContent value="verlauf" className="mt-4 space-y-3">
          {reports.length > 0 ? (
            reports.map((r, idx) => {
              const trend = getReportTrend(r, idx, reports);
              const isExpanded = expandedReport === r.id;
              return (
                <Card key={r.id} className="border border-slate-200 shadow-sm" data-testid={`report-card-${r.id}`}>
                  <CardContent className="p-0">
                    <button
                      className="w-full p-4 text-left flex items-start gap-3"
                      onClick={() => setExpandedReport(isExpanded ? null : r.id)}
                      data-testid={`report-toggle-${r.id}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-slate-500">#{reports.length - idx}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900">{formatDate(r.datum)}</p>
                          {trend && (
                            <Badge variant="outline" className={`text-[10px] border ${
                              trend.label === 'Besserung' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              trend.label === 'Verschlechterung' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {trend.label}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                          <span>{r.flaeche} cm²</span>
                          <span>NRS {r.schmerz_nrs}/10</span>
                          <span>{r.wundphase}</span>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3 animate-fade-in">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Größe (L x B x T)</p>
                            <p className="text-slate-700">{r.laenge} x {r.breite} x {r.tiefe} cm</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Wundphase</p>
                            <p className="text-slate-700">{r.wundphase || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Exsudat</p>
                            <p className="text-slate-700">{r.exsudat_menge || '-'} / {r.exsudat_art || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Schmerz</p>
                            <p className="text-slate-700">NRS {r.schmerz_nrs} ({r.schmerz_qualitaet || '-'})</p>
                          </div>
                        </div>

                        {r.wundgrund && (
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Wundgrund</p>
                            <div className="flex gap-2 flex-wrap">
                              {Object.entries(r.wundgrund).map(([key, val]) => val > 0 && (
                                <span key={key} className="text-xs bg-slate-100 px-2 py-1 rounded-md text-slate-600">
                                  {key.charAt(0).toUpperCase() + key.slice(1)}: {val}%
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {(r.infektionszeichen?.length > 0) && (
                          <div>
                            <p className="text-xs text-rose-500 uppercase tracking-wider mb-1">Infektionszeichen</p>
                            <div className="flex gap-1 flex-wrap">
                              {r.infektionszeichen.map((s, i) => (
                                <Badge key={i} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-xs">{s}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Behandlung</p>
                          <div className="text-sm text-slate-600 space-y-0.5">
                            {r.behandlung_reinigung && <p>Reinigung: {r.behandlung_reinigung}</p>}
                            {r.behandlung_primaerverband && <p>Primärverband: {r.behandlung_primaerverband}</p>}
                            {r.behandlung_sekundaerverband && <p>Sekundärverband: {r.behandlung_sekundaerverband}</p>}
                            {r.behandlung_lagerung && <p>Lagerung: {r.behandlung_lagerung}</p>}
                            {r.behandlung_kompression && <p>Kompression: {r.behandlung_kompression}</p>}
                            {r.schmerzmanagement && <p>Schmerzmanagement: {r.schmerzmanagement}</p>}
                            {r.vw_frequenz && <p>VW-Frequenz: {r.vw_frequenz}</p>}
                            {r.behandlung_begruendung && <p className="italic text-slate-500">Begründung: {r.behandlung_begruendung}</p>}
                          </div>
                        </div>

                        {(r.ursachen?.length > 0 || r.braden_skala > 0) && (
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Ursachen & Risiko</p>
                            <div className="text-sm text-slate-600 space-y-0.5">
                              {r.ursachen?.length > 0 && <p>Ursachen: {r.ursachen.join(', ')}</p>}
                              {r.braden_skala > 0 && <p>Braden: {r.braden_skala} Pkt.</p>}
                            </div>
                          </div>
                        )}

                        {r.pflegeziele?.length > 0 && (
                          <div>
                            <p className="text-xs text-teal-600 uppercase tracking-wider mb-1">Pflegeziele</p>
                            <p className="text-sm text-slate-600">{r.pflegeziele.join(', ')}</p>
                          </div>
                        )}

                        {r.arzt_informiert && (
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Ärztliche Einbindung</p>
                            <p className="text-sm text-slate-600">Informiert: {r.arzt_informiert}{r.arzt_anlass ? ` (${r.arzt_anlass})` : ''}</p>
                            {r.verordnung_vorhanden && <p className="text-sm text-slate-600">Verordnung: {r.verordnung_vorhanden}</p>}
                          </div>
                        )}

                        {r.evaluation_status && (
                          <div className={`rounded-lg p-2 ${r.evaluation_status === 'verbessert' ? 'bg-emerald-50' : r.evaluation_status === 'verschlechtert' ? 'bg-rose-50' : 'bg-amber-50'}`}>
                            <p className="text-xs uppercase tracking-wider font-bold">Evaluation: {r.evaluation_status}</p>
                            {r.evaluation_begruendung && <p className="text-sm mt-0.5">{r.evaluation_begruendung}</p>}
                          </div>
                        )}

                        {r.bemerkungen && (
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider">Bemerkungen</p>
                            <p className="text-sm text-slate-600 mt-0.5">{r.bemerkungen}</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-2 gap-2">
                          <p className="text-xs text-slate-400">PFK: {r.pflegefachkraft}</p>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleEditReport(e, r.id)}
                              className="h-8 text-xs"
                              data-testid={`edit-report-${r.id}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Bearbeiten
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleDeleteReport(e, r.id)}
                                className="h-8 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                data-testid={`delete-report-${r.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleExportPdf(r.id); }}
                              className="h-8 text-xs"
                              data-testid={`export-pdf-${r.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Noch keine Berichte vorhanden</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="fotos" className="mt-4">
          <WoundPhotoGallery woundId={id} />
        </TabsContent>

        <TabsContent value="trend" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-teal-700" />
                Heilungsverlauf
              </CardTitle>
            </CardHeader>
            <CardContent>
              <HealingTrendChart reports={reports} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
